import React, { useState, useEffect } from 'react';
import {
  Shield,
  Edit,
  Trash2,
  Plus,
  Search,
  Mail,
  Phone,
  CheckCircle,
  XCircle,
  AlertCircle,
  X,
  Calendar,
  Clock
} from 'lucide-react';
import { masterUsersApi, MasterUser, CreateMasterUserData } from '../services/api/adminAccountsApi';
import { MASTER_ROLES, MasterRole, getRoleDisplayName, getRoleAccessLevel } from '../constants/masterRoles';
import Button from './ui/Button';
import Input from './ui/Input';

const MasterUsersManagement: React.FC = () => {
  const [masterUsers, setMasterUsers] = useState<MasterUser[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<MasterUser[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingUser, setEditingUser] = useState<MasterUser | null>(null);
  const [deletingUser, setDeletingUser] = useState<MasterUser | null>(null);
  const [notification, setNotification] = useState<{
    type: 'success' | 'error' | 'info';
    message: string;
  } | null>(null);

  const [newUser, setNewUser] = useState<CreateMasterUserData>({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    phone: '',
    masterRole: 'PLATFORM_OWNER'
  });

  useEffect(() => {
    loadMasterUsers();
  }, []);

  useEffect(() => {
    filterUsers();
  }, [masterUsers, searchQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadMasterUsers = async () => {
    setIsLoading(true);
    try {
      const users = await masterUsersApi.getAll();
      setMasterUsers(users);
    } catch (error: any) {
      console.error('Error loading master users:', error);
      setNotification({
        type: 'error',
        message: error?.message || 'Failed to load master users'
      });
      setTimeout(() => setNotification(null), 5000);
    } finally {
      setIsLoading(false);
    }
  };

  const filterUsers = () => {
    let filtered = masterUsers;

    if (searchQuery) {
      filtered = filtered.filter(user =>
        user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.firstName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.lastName.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    setFilteredUsers(filtered);
  };

  const handleCreateMaster = async () => {
    try {
      if (!newUser.email || !newUser.password || !newUser.firstName || !newUser.lastName) {
        setNotification({
          type: 'error',
          message: 'Please fill in all required fields'
        });
        setTimeout(() => setNotification(null), 5000);
        return;
      }

      if (newUser.password.length < 8) {
        setNotification({
          type: 'error',
          message: 'Password must be at least 8 characters'
        });
        setTimeout(() => setNotification(null), 5000);
        return;
      }

      const createdUser = await masterUsersApi.create(newUser);
      setMasterUsers(prev => [...prev, createdUser]);
      setShowCreateModal(false);
      resetForm();
      setNotification({
        type: 'success',
        message: `Master user "${createdUser.email}" created successfully!`
      });
      setTimeout(() => setNotification(null), 5000);
    } catch (error: any) {
      console.error('Error creating master user:', error);
      setNotification({
        type: 'error',
        message: error?.message || 'Error creating master user. Email might already exist.'
      });
      setTimeout(() => setNotification(null), 5000);
    }
  };

  const handleEditMaster = async () => {
    if (!editingUser) return;

    try {
      const updatedUser = await masterUsersApi.update(editingUser.id, {
        email: editingUser.email,
        firstName: editingUser.firstName,
        lastName: editingUser.lastName,
        phone: editingUser.phone || undefined,
        masterRole: editingUser.masterRole
      });

      setMasterUsers(prev =>
        prev.map(user => user.id === editingUser.id ? updatedUser : user)
      );
      setShowEditModal(false);
      setEditingUser(null);
      setNotification({
        type: 'success',
        message: `Master user "${updatedUser.email}" updated successfully!`
      });
      setTimeout(() => setNotification(null), 5000);
    } catch (error: any) {
      console.error('Error updating master user:', error);
      setNotification({
        type: 'error',
        message: error?.message || 'Error updating master user. Please try again.'
      });
      setTimeout(() => setNotification(null), 5000);
    }
  };

  const handleDeleteMaster = async () => {
    if (!deletingUser) return;

    try {
      await masterUsersApi.delete(deletingUser.id);
      setMasterUsers(prev => prev.filter(user => user.id !== deletingUser.id));
      setShowDeleteModal(false);
      setDeletingUser(null);
      setNotification({
        type: 'success',
        message: `Master user "${deletingUser.email}" has been permanently deleted.`
      });
      setTimeout(() => setNotification(null), 5000);
    } catch (error: any) {
      console.error('Error deleting master user:', error);
      setNotification({
        type: 'error',
        message: error?.message || 'Error deleting master user. Please try again.'
      });
      setTimeout(() => setNotification(null), 5000);
    }
  };

  const resetForm = () => {
    setNewUser({
      email: '',
      password: '',
      firstName: '',
      lastName: '',
      phone: '',
      masterRole: 'PLATFORM_OWNER'
    });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Notification */}
      {notification && (
        <div className={`fixed top-4 right-4 z-50 max-w-md p-4 rounded-xl shadow-2xl transition-all duration-300 ${
          notification.type === 'success' ? 'bg-green-500/90 border border-green-400/50' :
          notification.type === 'error' ? 'bg-red-500/90 border border-red-400/50' :
          'bg-blue-500/90 border border-blue-400/50'
        } backdrop-blur-sm`}>
          <div className="flex items-center space-x-3">
            {notification.type === 'success' && <CheckCircle size={20} className="text-white" />}
            {notification.type === 'error' && <XCircle size={20} className="text-white" />}
            {notification.type === 'info' && <AlertCircle size={20} className="text-white" />}
            <span className="text-white font-medium">{notification.message}</span>
            <button
              onClick={() => setNotification(null)}
              className="text-white/70 hover:text-white"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Master Users</h2>
          <p className="text-white/60">Manage platform administrator accounts</p>
        </div>
        <Button
          onClick={() => setShowCreateModal(true)}
          className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700"
        >
          <Plus size={20} />
          Create Master User
        </Button>
      </div>

      {/* Search */}
      <div className="glass-card p-6 rounded-3xl">
        <Input
          placeholder="Search by email, first name, or last name..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          icon={<Search size={20} />}
          className="bg-white/10 border-white/20 text-white placeholder-white/50"
        />
      </div>

      {/* Master Users List */}
      <div className="glass-card rounded-3xl overflow-hidden">
        {/* Table Header */}
        <div className="bg-white/5 px-6 py-4 border-b border-white/10">
          <div className="grid grid-cols-12 gap-4 text-sm font-medium text-white/70">
            <div className="col-span-3">User</div>
            <div className="col-span-2">Contact</div>
            <div className="col-span-2">Role</div>
            <div className="col-span-3">Account Info</div>
            <div className="col-span-2">Actions</div>
          </div>
        </div>

        {/* Table Body */}
        <div className="divide-y divide-white/10">
          {filteredUsers.map((user) => (
            <div key={user.id} className="px-6 py-4 hover:bg-white/5 transition-colors duration-200">
              <div className="grid grid-cols-12 gap-4 items-center">
                {/* User Info */}
                <div className="col-span-3">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg flex items-center justify-center">
                      <Shield size={18} className="text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-white">{user.firstName} {user.lastName}</h3>
                      <p className="text-sm text-white/60">{user.email}</p>
                    </div>
                  </div>
                </div>

                {/* Contact Info */}
                <div className="col-span-2">
                  {user.phone ? (
                    <div className="flex items-center space-x-2 text-sm text-white/70">
                      <Phone size={12} />
                      <span>{user.phone}</span>
                    </div>
                  ) : (
                    <span className="text-sm text-white/40">No phone</span>
                  )}
                </div>

                {/* Role */}
                <div className="col-span-2">
                  {user.masterRole ? (
                    <div className="space-y-1">
                      <div className="inline-flex px-2 py-1 rounded-lg text-xs font-medium bg-purple-500/20 text-purple-400">
                        {getRoleDisplayName(user.masterRole as MasterRole)}
                      </div>
                      <div className="text-xs text-white/50">
                        {getRoleAccessLevel(user.masterRole as MasterRole)}
                      </div>
                    </div>
                  ) : (
                    <div className="inline-flex px-2 py-1 rounded-lg text-xs font-medium bg-purple-500/20 text-purple-400">
                      MASTER
                    </div>
                  )}
                </div>

                {/* Account Info */}
                <div className="col-span-3">
                  <div className="space-y-1 text-sm text-white/70">
                    <div className="flex items-center space-x-2">
                      <Calendar size={12} />
                      <span>Created: {formatDate(user.createdAt)}</span>
                    </div>
                    {(user as any).lastLoginAt && (
                      <div className="flex items-center space-x-2">
                        <Clock size={12} />
                        <span>Last login: {formatDate((user as any).lastLoginAt)}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="col-span-2">
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => {
                        setEditingUser(user);
                        setShowEditModal(true);
                      }}
                      className="p-2 glass-button rounded-lg hover:scale-105 transition-transform"
                    >
                      <Edit size={14} className="text-white/70 hover:text-white" />
                    </button>
                    
                    <button
                      onClick={() => {
                        setDeletingUser(user);
                        setShowDeleteModal(true);
                      }}
                      className="p-2 glass-button rounded-lg hover:scale-105 transition-transform text-red-400 hover:text-red-300"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {filteredUsers.length === 0 && (
        <div className="text-center py-12">
          <Shield size={48} className="text-white/30 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">No master users found</h3>
          <p className="text-white/60">Create your first master user to get started.</p>
        </div>
      )}

      {/* Create Master User Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="glass-card rounded-3xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-white">Create Master User</h3>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  resetForm();
                }}
                className="text-white/60 hover:text-white"
              >
                <X size={24} />
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-white font-medium mb-2">First Name *</label>
                  <input
                    type="text"
                    value={newUser.firstName}
                    onChange={(e) => setNewUser(prev => ({ ...prev, firstName: e.target.value }))}
                    className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500 focus-glass border border-gray-200"
                    placeholder="First name"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-white font-medium mb-2">Last Name *</label>
                  <input
                    type="text"
                    value={newUser.lastName}
                    onChange={(e) => setNewUser(prev => ({ ...prev, lastName: e.target.value }))}
                    className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500 focus-glass border border-gray-200"
                    placeholder="Last name"
                    required
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-white font-medium mb-2">Email *</label>
                <input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500 focus-glass border border-gray-200"
                  placeholder="master@example.com"
                  required
                />
              </div>
              
              <div>
                <label className="block text-white font-medium mb-2">Password *</label>
                <input
                  type="password"
                  value={newUser.password}
                  onChange={(e) => setNewUser(prev => ({ ...prev, password: e.target.value }))}
                  className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500 focus-glass border border-gray-200"
                  placeholder="Minimum 8 characters"
                  required
                />
                <p className="text-white/60 text-sm mt-1">Password must be at least 8 characters long</p>
              </div>
              
              <div>
                <label className="block text-white font-medium mb-2">Phone (Optional)</label>
                <input
                  type="tel"
                  value={newUser.phone || ''}
                  onChange={(e) => setNewUser(prev => ({ ...prev, phone: e.target.value }))}
                  className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500 focus-glass border border-gray-200"
                  placeholder="+962-6-123-4567"
                />
              </div>
              
              <div>
                <label className="block text-white font-medium mb-2">Role *</label>
                <select
                  value={newUser.masterRole || 'PLATFORM_OWNER'}
                  onChange={(e) => setNewUser(prev => ({ ...prev, masterRole: e.target.value as MasterRole }))}
                  className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 border border-gray-200 focus-glass"
                >
                  {Object.values(MASTER_ROLES).map((role) => (
                    <option key={role.id} value={role.id} className="bg-white text-gray-900">
                      {role.title} ({role.accessLevel})
                    </option>
                  ))}
                </select>
                {newUser.masterRole && (
                  <div className="mt-2 p-3 glass rounded-xl text-sm text-white/70">
                    <p className="font-medium mb-1">{MASTER_ROLES[newUser.masterRole as MasterRole].description}</p>
                    {MASTER_ROLES[newUser.masterRole as MasterRole].restrictions.length > 0 && (
                      <div className="mt-2">
                        <p className="text-xs font-medium text-white/50 mb-1">Restrictions:</p>
                        <ul className="list-disc list-inside text-xs space-y-1">
                          {MASTER_ROLES[newUser.masterRole as MasterRole].restrictions.map((restriction, idx) => (
                            <li key={idx}>{restriction}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            
            <div className="flex space-x-4 mt-8">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  resetForm();
                }}
                className="flex-1 px-6 py-3 glass rounded-xl text-white font-medium hover:scale-105 transition-transform"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateMaster}
                className="flex-1 glass-button px-6 py-3 rounded-xl text-white font-medium glow hover:scale-105 transition-transform"
              >
                <Plus size={20} className="mr-2" />
                Create Master User
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Master User Modal */}
      {showEditModal && editingUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="glass-card rounded-3xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-white">Edit Master User</h3>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditingUser(null);
                }}
                className="text-white/60 hover:text-white"
              >
                <X size={24} />
              </button>
            </div>
          
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-white font-medium mb-2">First Name *</label>
                  <input
                    type="text"
                    value={editingUser.firstName}
                    onChange={(e) => setEditingUser(prev => prev ? ({ ...prev, firstName: e.target.value }) : null)}
                    className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500 focus-glass border border-gray-200"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-white font-medium mb-2">Last Name *</label>
                  <input
                    type="text"
                    value={editingUser.lastName}
                    onChange={(e) => setEditingUser(prev => prev ? ({ ...prev, lastName: e.target.value }) : null)}
                    className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500 focus-glass border border-gray-200"
                    required
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-white font-medium mb-2">Email *</label>
                <input
                  type="email"
                  value={editingUser.email}
                  onChange={(e) => setEditingUser(prev => prev ? ({ ...prev, email: e.target.value }) : null)}
                  className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500 focus-glass border border-gray-200"
                  required
                />
              </div>
              
              <div>
                <label className="block text-white font-medium mb-2">Phone (Optional)</label>
                <input
                  type="tel"
                  value={editingUser.phone || ''}
                  onChange={(e) => setEditingUser(prev => prev ? ({ ...prev, phone: e.target.value }) : null)}
                  className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500 focus-glass border border-gray-200"
                  placeholder="+962-6-123-4567"
                />
              </div>
              
              <div>
                <label className="block text-white font-medium mb-2">Role *</label>
                <select
                  value={editingUser.masterRole || 'PLATFORM_OWNER'}
                  onChange={(e) => setEditingUser(prev => prev ? ({ ...prev, masterRole: e.target.value as MasterRole }) : null)}
                  className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 border border-gray-200 focus-glass"
                >
                  {Object.values(MASTER_ROLES).map((role) => (
                    <option key={role.id} value={role.id} className="bg-white text-gray-900">
                      {role.title} ({role.accessLevel})
                    </option>
                  ))}
                </select>
                {editingUser.masterRole && (
                  <div className="mt-2 p-3 glass rounded-xl text-sm text-white/70">
                    <p className="font-medium mb-1">{MASTER_ROLES[editingUser.masterRole as MasterRole].description}</p>
                    {MASTER_ROLES[editingUser.masterRole as MasterRole].restrictions.length > 0 && (
                      <div className="mt-2">
                        <p className="text-xs font-medium text-white/50 mb-1">Restrictions:</p>
                        <ul className="list-disc list-inside text-xs space-y-1">
                          {MASTER_ROLES[editingUser.masterRole as MasterRole].restrictions.map((restriction, idx) => (
                            <li key={idx}>{restriction}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          
            <div className="flex space-x-4 mt-8">
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditingUser(null);
                }}
                className="flex-1 px-6 py-3 glass rounded-xl text-white font-medium hover:scale-105 transition-transform"
              >
                Cancel
              </button>
              <button
                onClick={handleEditMaster}
                className="flex-1 glass-button px-6 py-3 rounded-xl text-white font-medium glow hover:scale-105 transition-transform"
              >
                <Edit size={20} className="mr-2" />
                Update Master User
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && deletingUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="glass-card rounded-3xl p-8 max-w-md w-full">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-white">Delete Master User</h3>
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeletingUser(null);
                }}
                className="text-white/60 hover:text-white"
              >
                <X size={24} />
              </button>
            </div>
            
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-r from-red-500 to-red-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <AlertCircle size={32} className="text-white" />
              </div>
              <p className="text-white/70 mb-2">
                Are you sure you want to delete <span className="font-semibold text-white">"{deletingUser.email}"</span>?
              </p>
              <p className="text-white/60 text-sm mb-4">
                This action cannot be undone.
              </p>
            </div>
            
            <div className="flex space-x-4">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeletingUser(null);
                }}
                className="flex-1 px-6 py-3 glass rounded-xl text-white font-medium hover:scale-105 transition-transform"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteMaster}
                className="flex-1 glass-button px-6 py-3 rounded-xl text-white font-medium glow hover:scale-105 transition-transform bg-gradient-to-r from-red-500 to-red-600"
              >
                <Trash2 size={20} className="mr-2" />
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MasterUsersManagement;

